// Motor de CRÉDITO REVOLVENTE (línea de crédito) — 100% frontend.
// Reutiliza la prelación de 7 niveles, la mora por tramos y los switches de IVA
// del motor de crédito simple (src/lib/operativo.ts).
import {
  IVA,
  aDate,
  aplicarPrelacion,
  diasEntre,
  fmtFechaCorta,
  fmtMXN,
  isoDe,
  moraCalculada,
  nuevoId,
  r2,
  r4,
  siguienteHabil,
  sumarDias,
  type AdeudoConceptos,
  type PasoAplicacion,
} from "./operativo";

export type PeriodicidadCorte = "MENSUAL" | "QUINCENAL" | "SEMANAL";
export type MetodoInteresRev = "Saldo promedio diario" | "Saldo al corte";

export const PERIODICIDADES_CORTE: PeriodicidadCorte[] = ["MENSUAL", "QUINCENAL", "SEMANAL"];
export const METODOS_INTERES_REV: MetodoInteresRev[] = ["Saldo promedio diario", "Saldo al corte"];

export interface ConfigRevolvente {
  productoId: string;
  fechaApertura: string;
  lineaAutorizada: number;
  periodicidadCorte: PeriodicidadCorte;
  diaCorte: number; // 1 a 28
  diasParaPagar: number;
  tasaAnual: number; // % anual
  tasaMoratoria: number; // % anual
  metodoInteres: MetodoInteresRev;
  ivaInteres: boolean;
  ivaMora: boolean;
  pctPagoMinimo: number; // % del saldo al corte
  pagoMinimoFijo: number; // piso en pesos
  comisionDisposicionPct: number; // % sobre cada disposición
  ivaComisionDisposicion: boolean;
  comisionAnualidad: number; // monto fijo anual
  ivaAnualidad: boolean;
  numImpagos: number; // pagos mínimos consecutivos no cubiertos → cartera vencida
  diasGraciaMora: number;
  bloquearDisposicionesEnMora: boolean;
}

export interface DisposicionRev {
  id: string;
  fecha: string;
  monto: number;
  concepto: string;
}

export interface PagoRev {
  id: string;
  fecha: string;
  monto: number;
  referencia: string;
}

export interface PenaRev {
  id: string;
  fecha: string;
  importe: number;
  causaIva: boolean;
  referencia: string;
}

export type EstatusRev = "SIN SALDO" | "AL CORRIENTE" | "PAGO MÍNIMO PENDIENTE" | "VENCIDO";

export interface MovimientoRev {
  fecha: string;
  tipo: "Disposición" | "Pago" | "Interés" | "Comisión" | "Mora" | "Pena" | "Rechazo";
  concepto: string;
  cargo: number;
  abono: number;
  saldoCapital: number;
  pasos?: PasoAplicacion[];
}

export interface PeriodoRev {
  numero: number;
  fechaInicio: string;
  fechaCorte: string;
  fechaLimite: string;
  dias: number;
  saldoAnterior: number;
  disposiciones: number;
  pagos: number;
  saldoPromedioDiario: number;
  interes: number;
  ivaInteres: number;
  comisiones: number;
  mora: number;
  ivaMora: number;
  pena: number;
  ivaPena: number;
  saldoCapitalAlCorte: number;
  saldoAlCorte: number;
  pagoMinimo: number;
  pagoNoGenerarIntereses: number;
  minimoPendiente: number;
  cubierto: boolean;
  impagosAcum: number;
  estatus: EstatusRev;
  cerrado: boolean;
  movimientos: MovimientoRev[];
}

export interface EstadoRevolvente {
  periodos: PeriodoRev[];
  periodoActual: PeriodoRev | null;
  ultimoCerrado: PeriodoRev | null;
  saldoCapital: number;
  lineaDisponible: number;
  utilizacionPct: number;
  backlog: AdeudoConceptos;
  adeudoTotal: number;
  moraDevengada: number;
  ivaMoraDevengada: number;
  /** Días de atraso corridos del pago mínimo no cubierto a la fecha de consulta. */
  diasMora: number;
  totalLiquidarHoy: number;
  /** Interés ordinario devengado del ciclo vigente (desde el último corte) sin IVA. */
  interesVigente: number;
  ivaInteresVigente: number;
  /** Interés del período vigente con IVA. */
  interesVigenteTotal: number;
  /** Total para ponerse al corriente (excluye el interés del período vigente). */
  totalAlCorriente: number;
  composicionAlCorriente: { label: string; monto: number }[];
  composicionLiquidar: { label: string; monto: number }[];
  minimoPendiente: number;
  fechaLimiteVigente: string | null;
  impagos: number;
  estatus: EstatusRev;
  proximoCorte: string;
  bloqueado: boolean;
  totales: {
    dispuesto: number;
    pagado: number;
    interes: number;
    ivaInteres: number;
    comisiones: number;
    mora: number;
    ivaMora: number;
    pena: number;
    ivaPena: number;
    capitalPagado: number;
  };
}

/* ------------------------------ Configuración ------------------------------ */
export const CONFIG_REVOLVENTE_DEFAULT: ConfigRevolvente = {
  productoId: "revolvente",
  fechaApertura: "2026-01-01",
  lineaAutorizada: 100000,
  periodicidadCorte: "MENSUAL",
  diaCorte: 15,
  diasParaPagar: 20,
  tasaAnual: 70,
  tasaMoratoria: 140,
  metodoInteres: "Saldo promedio diario",
  ivaInteres: true,
  ivaMora: true,
  pctPagoMinimo: 5,
  pagoMinimoFijo: 200,
  comisionDisposicionPct: 0,
  ivaComisionDisposicion: true,
  comisionAnualidad: 0,
  ivaAnualidad: true,
  numImpagos: 3,
  diasGraciaMora: 0,
  bloquearDisposicionesEnMora: true,
};

const nuevoAdeudo = (): AdeudoConceptos => ({
  pena: 0,
  ivaPena: 0,
  mora: 0,
  ivaMora: 0,
  interes: 0,
  ivaInteres: 0,
  capital: 0,
});

const sumaBacklog = (b: AdeudoConceptos) =>
  r2(b.pena + b.ivaPena + b.mora + b.ivaMora + b.interes + b.ivaInteres);

/* ------------------------------ Fechas de corte ---------------------------- */
export function fechasCorte(cfg: ConfigRevolvente, hasta: string, maximo = 240): string[] {
  const dia = Math.min(28, Math.max(1, Math.floor(cfg.diaCorte) || 1));
  const inicio = aDate(cfg.fechaApertura);
  const out: string[] = [];
  if (cfg.periodicidadCorte === "MENSUAL") {
    const y = inicio.getUTCFullYear();
    let m = inicio.getUTCMonth();
    if (diasEntre(cfg.fechaApertura, isoDe(new Date(Date.UTC(y, m, dia)))) <= 0) m += 1;
    for (let k = 0; k < maximo; k++) {
      const f = isoDe(new Date(Date.UTC(y, m + k, dia)));
      if (diasEntre(f, hasta) < 0) break;
      out.push(f);
    }
    return out;
  }
  const paso = cfg.periodicidadCorte === "QUINCENAL" ? 15 : 7;
  let f = sumarDias(cfg.fechaApertura, paso);
  for (let k = 0; k < maximo; k++) {
    if (diasEntre(f, hasta) < 0) break;
    out.push(f);
    f = sumarDias(f, paso);
  }
  return out;
}

/* --------------------------------- Simulación ------------------------------ */
type Evento =
  | { orden: 0; fecha: string; tipo: "disp"; d: DisposicionRev }
  | { orden: 1; fecha: string; tipo: "pago"; p: PagoRev }
  | { orden: 2; fecha: string; tipo: "corte"; numero: number };

/** Saldo promedio diario de capital a partir de los tramos de saldo del período. */
function promedioDiario(tramos: { desde: string; saldo: number }[], inicio: string, fin: string) {
  const dias = Math.max(1, diasEntre(inicio, fin));
  let acum = 0;
  for (let k = 0; k < dias; k++) {
    const dia = sumarDias(inicio, k + 1); // el saldo del día se mide al cierre del día
    let saldo = 0;
    for (const t of tramos) if (diasEntre(t.desde, dia) >= 0) saldo = t.saldo;
    acum += Math.max(0, saldo);
  }
  return { dias, promedio: r2(acum / dias) };
}

export function simularRevolvente(
  cfg: ConfigRevolvente,
  disposiciones: DisposicionRev[],
  pagos: PagoRev[],
  penas: PenaRev[],
  hoy: string,
  festivos: string[] = [],
): EstadoRevolvente {
  const cortes = fechasCorte(cfg, hoy);
  const eventos: Evento[] = [
    ...disposiciones
      .filter((d) => d.monto > 0 && d.fecha)
      .map((d) => ({ orden: 0 as const, fecha: d.fecha, tipo: "disp" as const, d })),
    ...pagos
      .filter((p) => p.monto > 0 && p.fecha)
      .map((p) => ({ orden: 1 as const, fecha: p.fecha, tipo: "pago" as const, p })),
    ...cortes.map((f, i) => ({
      orden: 2 as const,
      fecha: f,
      tipo: "corte" as const,
      numero: i + 1,
    })),
  ].sort((a, b) => a.fecha.localeCompare(b.fecha) || a.orden - b.orden);

  const backlog = nuevoAdeudo();
  let saldoCapital = 0;
  let impagos = 0;
  let minimoPendiente = 0;
  let fechaLimiteVigente: string | null = null;
  let cursorMora: string | null = null;
  let inicioPeriodo = cfg.fechaApertura;
  let tramos: { desde: string; saldo: number }[] = [{ desde: cfg.fechaApertura, saldo: 0 }];
  let dispPeriodo = 0;
  let pagosPeriodo = 0;
  let comisionesPeriodo = 0;
  let moraPeriodo = 0;
  let ivaMoraPeriodo = 0;
  let penaPeriodo = 0;
  let ivaPenaPeriodo = 0;
  let movimientos: MovimientoRev[] = [];
  let saldoAnterior = 0;
  const periodos: PeriodoRev[] = [];
  const penasCargadas = new Set<string>();
  const totales = {
    dispuesto: 0,
    pagado: 0,
    interes: 0,
    ivaInteres: 0,
    comisiones: 0,
    mora: 0,
    ivaMora: 0,
    pena: 0,
    ivaPena: 0,
    capitalPagado: 0,
  };

  const vencido = () => impagos >= Math.max(1, Math.floor(cfg.numImpagos) || 1);
  const disponibleEn = () => r2(Math.max(0, cfg.lineaAutorizada - saldoCapital));
  const marcaSaldo = (fecha: string) => {
    tramos = [...tramos, { desde: fecha, saldo: saldoCapital }];
  };

  const estatusDe = (): EstatusRev => {
    if (vencido()) return "VENCIDO";
    if (saldoCapital <= 0.01 && sumaBacklog(backlog) <= 0.01 && minimoPendiente <= 0.01)
      return "SIN SALDO";
    if (minimoPendiente > 0.01) return "PAGO MÍNIMO PENDIENTE";
    return "AL CORRIENTE";
  };

  for (const ev of eventos) {
    if (diasEntre(ev.fecha, hoy) < 0) continue; // el evento aún no ocurre en la línea de tiempo

    if (ev.tipo === "disp") {
      const bloqueado = cfg.bloquearDisposicionesEnMora && vencido();
      const disponible = disponibleEn();
      if (bloqueado || ev.d.monto > disponible + 0.004) {
        movimientos.push({
          fecha: ev.fecha,
          tipo: "Rechazo",
          concepto: bloqueado
            ? `Disposición rechazada (línea bloqueada por cartera vencida): ${ev.d.concepto || "sin concepto"}`
            : `Disposición rechazada (excede línea disponible ${disponible.toFixed(2)}): ${ev.d.concepto || "sin concepto"}`,
          cargo: 0,
          abono: 0,
          saldoCapital: r2(saldoCapital),
        });
        continue;
      }
      saldoCapital = r2(saldoCapital + ev.d.monto);
      dispPeriodo = r2(dispPeriodo + ev.d.monto);
      totales.dispuesto = r2(totales.dispuesto + ev.d.monto);
      marcaSaldo(ev.fecha);
      movimientos.push({
        fecha: ev.fecha,
        tipo: "Disposición",
        concepto: ev.d.concepto || "Disposición de línea",
        cargo: r2(ev.d.monto),
        abono: 0,
        saldoCapital: r2(saldoCapital),
      });
      const com = r2(
        ((ev.d.monto * (Number(cfg.comisionDisposicionPct) || 0)) / 100) *
          (cfg.ivaComisionDisposicion ? 1 + IVA : 1),
      );
      if (com > 0) {
        saldoCapital = r2(saldoCapital + com);
        comisionesPeriodo = r2(comisionesPeriodo + com);
        totales.comisiones = r2(totales.comisiones + com);
        marcaSaldo(ev.fecha);
        movimientos.push({
          fecha: ev.fecha,
          tipo: "Comisión",
          concepto: `Comisión por disposición ${cfg.comisionDisposicionPct}%${cfg.ivaComisionDisposicion ? " + IVA" : ""}`,
          cargo: com,
          abono: 0,
          saldoCapital: r2(saldoCapital),
        });
      }
      continue;
    }

    if (ev.tipo === "pago") {
      // Mora por tramos incrementales: solo desde el vencimiento del pago mínimo no cubierto.
      let mora = 0;
      let ivaMora = 0;
      if (cursorMora && minimoPendiente > 0.01) {
        const desde = cursorMora;
        const dias = Math.max(0, diasEntre(desde, ev.fecha));
        if (dias > 0) {
          mora = r2(moraCalculada(minimoPendiente, (Number(cfg.tasaMoratoria) || 0) / 100, dias));
          ivaMora = cfg.ivaMora ? r2(mora * IVA) : 0;
          if (mora > 0)
            movimientos.push({
              fecha: ev.fecha,
              tipo: "Mora",
              concepto: `Interés moratorio ${dias} día(s) desde ${fmtFechaCorta(desde)} sobre el pago mínimo no cubierto (${fmtMXN(minimoPendiente)})`,
              cargo: r2(mora + ivaMora),
              abono: 0,
              saldoCapital: r2(saldoCapital),
            });
          cursorMora = ev.fecha;
        }
      }
      moraPeriodo = r2(moraPeriodo + mora);
      ivaMoraPeriodo = r2(ivaMoraPeriodo + ivaMora);
      totales.mora = r2(totales.mora + mora);
      totales.ivaMora = r2(totales.ivaMora + ivaMora);

      // Penas convencionales capturadas manualmente y ya devengadas.
      let pena = 0;
      let ivaPena = 0;
      for (const pn of penas) {
        if (penasCargadas.has(pn.id) || pn.importe <= 0) continue;
        if (diasEntre(pn.fecha, ev.fecha) < 0) continue;
        penasCargadas.add(pn.id);
        pena = r2(pena + pn.importe);
        if (pn.causaIva) ivaPena = r2(ivaPena + pn.importe * IVA);
      }
      penaPeriodo = r2(penaPeriodo + pena);
      ivaPenaPeriodo = r2(ivaPenaPeriodo + ivaPena);
      totales.pena = r2(totales.pena + pena);
      totales.ivaPena = r2(totales.ivaPena + ivaPena);

      const adeudo: AdeudoConceptos = {
        pena: r4(backlog.pena + pena),
        ivaPena: r4(backlog.ivaPena + ivaPena),
        mora: r4(backlog.mora + mora),
        ivaMora: r4(backlog.ivaMora + ivaMora),
        interes: r4(backlog.interes),
        ivaInteres: r4(backlog.ivaInteres),
        capital: r4(saldoCapital),
      };
      const res = aplicarPrelacion(ev.p.monto, adeudo);
      const capitalPagado = r4(adeudo.capital - res.restante.capital);
      backlog.pena = res.restante.pena;
      backlog.ivaPena = res.restante.ivaPena;
      backlog.mora = res.restante.mora;
      backlog.ivaMora = res.restante.ivaMora;
      backlog.interes = res.restante.interes;
      backlog.ivaInteres = res.restante.ivaInteres;
      saldoCapital = r2(Math.max(0, res.restante.capital));
      const aplicado = r2(Math.max(0, ev.p.monto - res.sobrepago));
      minimoPendiente = r2(Math.max(0, minimoPendiente - aplicado));
      totales.pagado = r2(totales.pagado + aplicado);
      totales.capitalPagado = r2(totales.capitalPagado + capitalPagado);
      pagosPeriodo = r2(pagosPeriodo + aplicado);
      if (minimoPendiente <= 0.01) {
        cursorMora = null;
        fechaLimiteVigente = null;
        // Estatus derivado: cubrir el mínimo (aun tarde) pone la línea al corriente.
        impagos = 0;
      }
      marcaSaldo(ev.fecha);
      movimientos.push({
        fecha: ev.fecha,
        tipo: "Pago",
        concepto: `Pago recibido${ev.p.referencia ? ` · ${ev.p.referencia}` : ""}${
          capitalPagado > 0 ? ` · libera ${fmtMXN(capitalPagado)} de línea disponible` : ""
        }`,
        cargo: 0,
        abono: r2(ev.p.monto),
        saldoCapital: r2(saldoCapital),
        pasos: res.pasos,
      });
      continue;
    }

    // ------------------------------ CORTE ------------------------------
    const corte = ev.fecha;
    const { dias, promedio } = promedioDiario(tramos, inicioPeriodo, corte);
    const tasaDiaria = (Number(cfg.tasaAnual) || 0) / 100 / 360;
    const base = cfg.metodoInteres === "Saldo al corte" ? saldoCapital : promedio;
    const interes = r2(base * tasaDiaria * dias);
    const ivaInteres = cfg.ivaInteres ? r2(interes * IVA) : 0;
    backlog.interes = r2(backlog.interes + interes);
    backlog.ivaInteres = r2(backlog.ivaInteres + ivaInteres);
    totales.interes = r2(totales.interes + interes);
    totales.ivaInteres = r2(totales.ivaInteres + ivaInteres);
    if (interes > 0)
      movimientos.push({
        fecha: corte,
        tipo: "Interés",
        concepto:
          cfg.metodoInteres === "Saldo al corte"
            ? `Interés del período (saldo al corte ${fmtMXN(saldoCapital)} × ${dias} días)`
            : `Interés del período (saldo promedio diario ${fmtMXN(promedio)} × ${dias} días)`,
        cargo: r2(interes + ivaInteres),
        abono: 0,
        saldoCapital: r2(saldoCapital),
      });

    // Comisión por anualidad en el corte que cumple aniversario.
    const mesesTranscurridos = Math.round(diasEntre(cfg.fechaApertura, corte) / 30.4375);
    const anualidadAplica =
      (Number(cfg.comisionAnualidad) || 0) > 0 &&
      cfg.periodicidadCorte === "MENSUAL" &&
      mesesTranscurridos > 0 &&
      mesesTranscurridos % 12 === 0;
    if (anualidadAplica) {
      const anual = r2(cfg.comisionAnualidad * (cfg.ivaAnualidad ? 1 + IVA : 1));
      saldoCapital = r2(saldoCapital + anual);
      comisionesPeriodo = r2(comisionesPeriodo + anual);
      totales.comisiones = r2(totales.comisiones + anual);
      marcaSaldo(corte);
      movimientos.push({
        fecha: corte,
        tipo: "Comisión",
        concepto: `Comisión por anualidad${cfg.ivaAnualidad ? " + IVA" : ""}`,
        cargo: anual,
        abono: 0,
        saldoCapital: r2(saldoCapital),
      });
    }

    // Impago: el pago mínimo del período anterior no quedó cubierto.
    const huboMinimo = fechaLimiteVigente !== null;
    // Cristaliza la mora devengada del mínimo no cubierto hasta la fecha de corte
    // (el calendario ya la generó, aunque no haya habido pagos en el período).
    const veniaVencido = minimoPendiente > 0.01;
    if (cursorMora && veniaVencido) {
      const diasM = Math.max(0, diasEntre(cursorMora, corte));
      if (diasM > 0) {
        const moraC = r2(
          moraCalculada(minimoPendiente, (Number(cfg.tasaMoratoria) || 0) / 100, diasM),
        );
        const ivaMoraC = cfg.ivaMora ? r2(moraC * IVA) : 0;
        if (moraC > 0) {
          backlog.mora = r2(backlog.mora + moraC);
          backlog.ivaMora = r2(backlog.ivaMora + ivaMoraC);
          moraPeriodo = r2(moraPeriodo + moraC);
          ivaMoraPeriodo = r2(ivaMoraPeriodo + ivaMoraC);
          totales.mora = r2(totales.mora + moraC);
          totales.ivaMora = r2(totales.ivaMora + ivaMoraC);
          movimientos.push({
            fecha: corte,
            tipo: "Mora",
            concepto: `Interés moratorio devengado ${diasM} día(s) desde ${fmtFechaCorta(cursorMora)} sobre el pago mínimo no cubierto (${fmtMXN(minimoPendiente)})`,
            cargo: r2(moraC + ivaMoraC),
            abono: 0,
            saldoCapital: r2(saldoCapital),
          });
        }
      }
    }
    if (huboMinimo) {
      if (minimoPendiente > 0.01) impagos += 1;
      else impagos = 0;
    }

    const saldoCapitalAlCorte = r2(saldoCapital);
    const saldoAlCorte = r2(saldoCapitalAlCorte + sumaBacklog(backlog));
    const minimoCalculado = r2(
      Math.max(
        ((Number(cfg.pctPagoMinimo) || 0) / 100) * saldoCapitalAlCorte +
          interes +
          ivaInteres +
          comisionesPeriodo,
        Number(cfg.pagoMinimoFijo) || 0,
      ),
    );
    const pagoMinimo =
      saldoAlCorte <= 0.01 ? 0 : r2(Math.min(saldoAlCorte, minimoCalculado + minimoPendiente));
    const fechaLimite = siguienteHabil(
      sumarDias(corte, Math.max(0, Math.floor(cfg.diasParaPagar) || 0)),
      festivos,
    );

    periodos.push({
      numero: ev.numero,
      fechaInicio: inicioPeriodo,
      fechaCorte: corte,
      fechaLimite,
      dias,
      saldoAnterior,
      disposiciones: dispPeriodo,
      pagos: pagosPeriodo,
      saldoPromedioDiario: promedio,
      interes,
      ivaInteres,
      comisiones: comisionesPeriodo,
      mora: moraPeriodo,
      ivaMora: ivaMoraPeriodo,
      pena: penaPeriodo,
      ivaPena: ivaPenaPeriodo,
      saldoCapitalAlCorte,
      saldoAlCorte,
      pagoMinimo,
      pagoNoGenerarIntereses: saldoAlCorte,
      minimoPendiente: pagoMinimo,
      cubierto: pagoMinimo <= 0.01,
      impagosAcum: impagos,
      estatus:
        saldoAlCorte <= 0.01
          ? vencido()
            ? "VENCIDO"
            : "SIN SALDO"
          : vencido()
            ? "VENCIDO"
            : "AL CORRIENTE",
      cerrado: true,
      movimientos,
    });

    minimoPendiente = pagoMinimo;
    fechaLimiteVigente = pagoMinimo > 0.01 ? fechaLimite : null;
    // Si ya venía vencido, la mora sigue corriendo sin interrupción desde el corte;
    // si no, arranca al vencer la nueva fecha límite (más días de gracia).
    cursorMora =
      pagoMinimo > 0.01
        ? veniaVencido
          ? corte
          : sumarDias(fechaLimite, Math.max(0, Math.floor(cfg.diasGraciaMora) || 0))
        : null;
    saldoAnterior = saldoCapitalAlCorte;
    inicioPeriodo = corte;
    tramos = [{ desde: corte, saldo: saldoCapital }];
    dispPeriodo = 0;
    pagosPeriodo = 0;
    comisionesPeriodo = 0;
    moraPeriodo = 0;
    ivaMoraPeriodo = 0;
    penaPeriodo = 0;
    ivaPenaPeriodo = 0;
    movimientos = [];
  }

  // Mora devengada a la fecha simulada (aún no aplicada a ningún pago).
  let moraDevengada = 0;
  let ivaMoraDevengada = 0;
  let diasMora = 0;
  if (cursorMora && minimoPendiente > 0.01) {
    const dias = Math.max(0, diasEntre(cursorMora, hoy));
    diasMora = dias;
    if (dias > 0) {
      moraDevengada = r2(
        moraCalculada(minimoPendiente, (Number(cfg.tasaMoratoria) || 0) / 100, dias),
      );
      ivaMoraDevengada = cfg.ivaMora ? r2(moraDevengada * IVA) : 0;
    }
  }

  const ultimoCerrado = periodos.length ? periodos[periodos.length - 1]! : null;
  const proximoCorte =
    fechasCorte(cfg, sumarDias(hoy, 400)).find((f) => diasEntre(hoy, f) > 0) ?? hoy;
  const periodoActual: PeriodoRev | null = {
    numero: periodos.length + 1,
    fechaInicio: inicioPeriodo,
    fechaCorte: proximoCorte,
    fechaLimite: siguienteHabil(
      sumarDias(proximoCorte, Math.max(0, Math.floor(cfg.diasParaPagar) || 0)),
      festivos,
    ),
    dias: Math.max(0, diasEntre(inicioPeriodo, hoy)),
    saldoAnterior,
    disposiciones: dispPeriodo,
    pagos: pagosPeriodo,
    saldoPromedioDiario: promedioDiario(tramos, inicioPeriodo, hoy).promedio,
    interes: 0,
    ivaInteres: 0,
    comisiones: comisionesPeriodo,
    mora: moraPeriodo,
    ivaMora: ivaMoraPeriodo,
    pena: penaPeriodo,
    ivaPena: ivaPenaPeriodo,
    saldoCapitalAlCorte: r2(saldoCapital),
    saldoAlCorte: r2(saldoCapital + sumaBacklog(backlog)),
    pagoMinimo: r2(minimoPendiente),
    pagoNoGenerarIntereses: r2(saldoCapital + sumaBacklog(backlog)),
    minimoPendiente: r2(minimoPendiente),
    cubierto: minimoPendiente <= 0.01,
    impagosAcum: impagos,
    estatus: estatusDe(),
    cerrado: false,
    movimientos,
  };

  const adeudoTotal = r2(sumaBacklog(backlog) + moraDevengada + ivaMoraDevengada);

  // Interés devengado del ciclo vigente (desde el último corte hasta la fecha simulada).
  const tasaDiariaVig = (Number(cfg.tasaAnual) || 0) / 100 / 360;
  const diasVig = Math.max(0, diasEntre(inicioPeriodo, hoy));
  const baseVig =
    cfg.metodoInteres === "Saldo al corte"
      ? saldoCapital
      : promedioDiario(tramos, inicioPeriodo, hoy).promedio;
  const interesVigente = saldoCapital > 0.01 ? r2(baseVig * tasaDiariaVig * diasVig) : 0;
  const ivaInteresVigente = cfg.ivaInteres ? r2(interesVigente * IVA) : 0;
  const interesVigenteTotal = r2(interesVigente + ivaInteresVigente);
  const totalAlCorriente = r2(minimoPendiente + moraDevengada + ivaMoraDevengada);
  const composicionAlCorriente = [
    { label: "Pago mínimo pendiente", monto: r2(minimoPendiente) },
    { label: "Moratorios devengados (c/IVA)", monto: r2(moraDevengada + ivaMoraDevengada) },
  ];
  const composicionLiquidar = [
    { label: "Saldo de capital", monto: r2(saldoCapital) },
    { label: "Intereses y comisiones vencidos (c/IVA)", monto: r2(sumaBacklog(backlog)) },
    { label: "Interés del período vigente (c/IVA)", monto: interesVigenteTotal },
    { label: "Moratorios devengados (c/IVA)", monto: r2(moraDevengada + ivaMoraDevengada) },
  ];
  return {
    periodos,
    periodoActual,
    ultimoCerrado,
    saldoCapital: r2(saldoCapital),
    lineaDisponible: disponibleEn(),
    utilizacionPct: cfg.lineaAutorizada > 0 ? r2((saldoCapital / cfg.lineaAutorizada) * 100) : 0,
    backlog: { ...backlog, capital: r2(saldoCapital) },
    adeudoTotal,
    moraDevengada,
    ivaMoraDevengada,
    diasMora,
    totalLiquidarHoy: r2(saldoCapital + adeudoTotal + interesVigenteTotal),
    interesVigente,
    ivaInteresVigente,
    interesVigenteTotal,
    totalAlCorriente,
    composicionAlCorriente,
    composicionLiquidar,
    minimoPendiente: r2(minimoPendiente),
    fechaLimiteVigente,
    impagos,
    estatus: estatusDe(),
    proximoCorte,
    bloqueado: cfg.bloquearDisposicionesEnMora && impagos >= Math.max(1, cfg.numImpagos),
    totales,
  };
}

/* ------------------------------ Escenario demo ----------------------------- */
export interface EscenarioRev {
  config: ConfigRevolvente;
  disposiciones: DisposicionRev[];
  pagos: PagoRev[];
  penas: PenaRev[];
  hoy: string;
}

/**
 * Demo guiado: dos disposiciones, pago mínimo exacto a tiempo, pago tardío parcial
 * con mora sobre el pago mínimo no cubierto y liquidación total (línea 100% libre).
 */
export function escenarioDemoRevolvente(): EscenarioRev {
  const config: ConfigRevolvente = {
    ...CONFIG_REVOLVENTE_DEFAULT,
    lineaAutorizada: 100000,
    fechaApertura: "2026-01-01",
  };
  const disposiciones: DisposicionRev[] = [
    {
      id: nuevoId("disp"),
      fecha: "2026-01-01",
      monto: 40000,
      concepto: "Disposición inicial capital de trabajo",
    },
    {
      id: nuevoId("disp"),
      fecha: "2026-02-05",
      monto: 30000,
      concepto: "Segunda disposición inventario",
    },
  ];
  const pagos: PagoRev[] = [];
  const penas: PenaRev[] = [];

  // 1er corte (15-ene): pago del mínimo exacto y a tiempo.
  const e1 = simularRevolvente(config, disposiciones, pagos, penas, "2026-01-16");
  const p1 = e1.ultimoCerrado!;
  pagos.push({
    id: nuevoId("pagorev"),
    fecha: sumarDias(p1.fechaLimite, -2),
    monto: p1.pagoMinimo,
    referencia: "Pago mínimo período 1 (a tiempo)",
  });

  // 2° corte (15-feb): pago del mínimo exacto y a tiempo.
  const e2 = simularRevolvente(config, disposiciones, pagos, penas, "2026-02-16");
  const p2 = e2.ultimoCerrado!;
  pagos.push({
    id: nuevoId("pagorev"),
    fecha: sumarDias(p2.fechaLimite, -1),
    monto: p2.pagoMinimo,
    referencia: "Pago mínimo período 2 (a tiempo)",
  });

  // 3er corte (15-mar): pago tardío y parcial → mora sobre el mínimo no cubierto.
  const e3 = simularRevolvente(config, disposiciones, pagos, penas, "2026-03-16");
  const p3 = e3.ultimoCerrado!;
  pagos.push({
    id: nuevoId("pagorev"),
    fecha: sumarDias(p3.fechaLimite, 9),
    monto: r2(p3.pagoMinimo * 0.5),
    referencia: "Pago tardío parcial período 3 (genera mora)",
  });

  // Liquidación total: se ajusta por iteración al total a liquidar del día.
  const fechaLiq = "2026-04-20";
  let liquidacion = 0;
  const pagoLiq: PagoRev = {
    id: nuevoId("pagorev"),
    fecha: fechaLiq,
    monto: 0,
    referencia: "Liquidación total de la línea",
  };
  pagos.push(pagoLiq);
  for (let k = 0; k < 8; k++) {
    pagoLiq.monto = liquidacion;
    const est = simularRevolvente(config, disposiciones, pagos, penas, fechaLiq);
    if (est.totalLiquidarHoy <= 0.01) break;
    liquidacion = r2(liquidacion + est.totalLiquidarHoy);
  }
  pagoLiq.monto = liquidacion;

  return { config, disposiciones, pagos, penas, hoy: fechaLiq };
}
